import { Button, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useCallback, useState } from "react";
import { APIClient, BTC_NETWORK } from "../constants";
import { useWalletContext } from "../context/wallet";
import { useFeeEstimate } from "../hooks/useFeeEstimate";
import { walletProvider } from "../services/wallet";
import { buildMempoolTxnLink, errorToMessage } from "../utils";

export const DepositButton = ({ tokenIds }: { tokenIds: string[] }) => {
  const { multiSigWallet, ordinalsPubkey, paymentPubkey, ordinalsAddress, ordinalsAddressType } =
    useWalletContext();
  const feeEstimate = useFeeEstimate();
  const [loading, setLoading] = useState(false);
  const handleDeposit = useCallback(async () => {
    if (
      !multiSigWallet.data ||
      !ordinalsPubkey ||
      !ordinalsAddress ||
      !paymentPubkey ||
      !ordinalsAddressType
    ) {
      return;
    }
    try {
      setLoading(true);

      // Fetch token deposit PSBT
      const { data } = await APIClient.get(
        `/mswallets/${multiSigWallet.data.address}/psbt/deposit`,
        {
          params: {
            depositorPublicKey: ordinalsPubkey,
            tokenIds,
            depositorPaymentType: ordinalsAddressType,
            outputs: [],
            feeSatsPerVbyte:
              BTC_NETWORK === "mainnet"
                ? feeEstimate.data?.halfHourFee?.satsPerVb ?? 15
                : 10,
            minTotalDummyUtxoPair: 0,
            msWalletOwnerPublicKey: paymentPubkey,
          },
        }
      );

      // Sign token deposit PSBT
      const { signedPsbt } = await walletProvider.signTransaction(
        data.psbt,
        [
          {
            indexesToSign: data.toSignInputs,
            address: ordinalsAddress,
          },
        ]
      );

      // Submit signed token deposit PSBT
      const { data: depositSubmitData } = await APIClient.post(
        `/mswallets/${multiSigWallet.data.address}/deposit`,
        {
          psbt: signedPsbt,
          signerPaymentType: ordinalsAddressType,
          signerPublicKey: ordinalsPubkey,
          msWalletOwnerPublicKey: paymentPubkey,
        }
      );

      if (depositSubmitData) {
        notifications.show({
          autoClose: 15000,
          title: "Deposit submitted",
          message: (
            <Text
              component="a"
              color="blue"
              underline
              href={buildMempoolTxnLink(depositSubmitData.transactionId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View transaction
            </Text>
          ),
        });
      }
    } catch (err) {
      notifications.show({
        color: "red",
        autoClose: false,
        message: errorToMessage(err),
        title: "Deposit failed",
      });
    } finally {
      setLoading(false);
    }
  }, [
    feeEstimate.data?.halfHourFee?.satsPerVb,
    multiSigWallet.data,
    ordinalsAddress,
    ordinalsPubkey,
    paymentPubkey,
    ordinalsAddressType,
    tokenIds,
  ]);

  return (
    <Button loading={loading} onClick={handleDeposit}>
      {loading ? "Loading" : "Deposit to multisig"}
    </Button>
  );
};
